/**
 * Protocol Generator Wizard Page
 * Multi-step wizard for protocol modification workflow
 *
 * Session-based: Automatically creates/reuses sessions for PRS version pairs.
 * Includes commit/discard flow for execution results.
 */

import React, {useState, useEffect, useRef, useCallback, useMemo} from 'react';
import {useParams, useNavigate, useLocation} from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ArrowForward as ForwardIcon,
  Download as DownloadIcon,
  CheckCircle as CommitIcon,
  Delete as DiscardIcon,
  WarningAmber as WarningIcon,
  AccountTreeOutlined as AccountTreeIcon,
  PlayCircleOutline as PlayCircleIcon,
  PlayArrow as PlayArrowIcon,
  VerifiedOutlined as VerifiedIcon,
} from '@mui/icons-material';
import {
  Dialog as ModularDialog,
  ProgressOverlay,
  Notification,
} from '@signanthealth/modular-ui';
import {WizardSteps} from '../components/WizardSteps';
import {RequirementsEditor} from '../components/RequirementsEditor';
import {InstructionsEditor} from '../components/InstructionsEditor';
import {ComparisonDialog} from '../components/ComparisonDialog';
import {RequirementsGenerationDialog} from '../components/RequirementsGenerationDialog';
import {PlanGenerationDialog} from '../components/PlanGenerationDialog';
import {ProtocolExecutionDialog} from '../components/ProtocolExecutionDialog';
import {PlanActionsTable} from '../components/PlanActionsTable';
import {ExecutionSummaryTable} from '../components/ExecutionSummaryTable';
import {XMLDiffDialog} from '../components/XMLDiffDialog';
import {TrialTopBar} from '../components/TrialTopBar';
import {PlatformTabsLayout} from '../components/PlatformTabsLayout';
import {apiService} from '../services/api';
import {useDispatcherJob} from '../hooks/useDispatcherJob';
import {saveAs} from 'file-saver';
import type {ExecutionRunningStatus} from '../types';
import { WizardNavigationProvider } from '../contexts/WizardNavigationContext';
import {
  parseWizardTrailingPath,
  buildWizardPath,
  getWizardTrailingPathname,
  wizardSegmentFromStepIndex,
  wizardStepIndexFromSegment,
} from '../utils/wizardStepRoutes';

const WIZARD_STEPS = ['Requirements', 'Plan', 'Generate', 'Validate'];

const WizardPageContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {trialId, version, platform} = useParams<{
    trialId: string;
    version: string;
    platform: 'app' | 'web';
  }>();

  const baseWizardPath = useMemo(
    () => (trialId && version && platform ? `/wizard/${trialId}/${version}/${platform}` : ''),
    [trialId, version, platform]
  );
  const trailing = useMemo(
    () => (baseWizardPath ? getWizardTrailingPathname(location.pathname, baseWizardPath) : ''),
    [location.pathname, baseWizardPath]
  );
  const pathSessionId = useMemo(
    () => parseWizardTrailingPath(trailing).sessionId,
    [trailing]
  );

  // Get navigation state from parent page (ProtocolList / tabs)
  const routeState = (location.state as any) || {};
  const routePlatforms = routeState.platforms as string[] | undefined;
  const parentSessionId = routeState.sessionId as string | undefined;

  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [baseVersion, setBaseVersion] = useState<string | null>(null);
  const [targetVersion, setTargetVersion] = useState<string | null>(null); // Last version (by date)
  
  // Trial platforms - fetched from API when navigation state is not available (direct URL access)
  const [fetchedPlatforms, setFetchedPlatforms] = useState<('app' | 'web')[] | null>(null);
  const trialPlatforms: ('app' | 'web')[] =
    routePlatforms?.length ? routePlatforms as ('app' | 'web')[] 
    : fetchedPlatforms?.length ? fetchedPlatforms 
    : [platform as 'app' | 'web'];

  // Step data
  const [requirements, setRequirements] = useState<any>(null);
  const [plan, setPlan] = useState<any>(null);
  const [comparisonData, setComparisonData] = useState<any>(null);
  const [comparisonDialogOpen, setComparisonDialogOpen] = useState(false);

  // Requirements generation dialog
  const [reqGenerationDialogOpen, setReqGenerationDialogOpen] = useState(false);
  const [reqGenerationJobId] = useState<string | null>(null);

  // Plan generation dialog
  const [planGenerationDialogOpen, setPlanGenerationDialogOpen] = useState(false);
  const [planGenerationJobId] = useState<string | null>(null);

  // Protocol execution dialog
  const [executionDialogOpen, setExecutionDialogOpen] = useState(false);
  const [executionJobId] = useState<string | null>(null);
  const [protocolGenerated, setProtocolGenerated] = useState(false);

  // Execution summary
  const [executionSummary, setExecutionSummary] = useState<any>(null);
  // When summary has total_actions=0 and getExecutionRunningStatus returns "running"
  const [executionRunningStatus, setExecutionRunningStatus] = useState<ExecutionRunningStatus | null>(null);

  // XML Diff dialog
  const [diffDialogOpen, setDiffDialogOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<any>(null);

  // Download state
  const [downloading, setDownloading] = useState(false);

  // Session state
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [, setSessionCreated] = useState(false);

  // Execution lifecycle state
  const [executionId, setExecutionId] = useState<string | null>(null);
  const [isExecutionCommitted, setIsExecutionCommitted] = useState(false);
  const [isCommitting, setIsCommitting] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);

  // Confirmation dialogs
  const [commitDialogOpen, setCommitDialogOpen] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

  // Notification state
  const [snackbar, setSnackbar] = useState<{open: boolean; message: string; severity: 'success' | 'error'}>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Workflow mode state
  const [wizardBlocked, setWizardBlocked] = useState(false);
  const [wizardBlockedReason, setWizardBlockedReason] = useState<string | null>(null);

  const getRequirementCount = (requirementsData: any): number => {
    if (!requirementsData?.requirements) return 0;
    let rawRequirements = requirementsData.requirements;
    if (rawRequirements && typeof rawRequirements === 'object' && rawRequirements.requirements) {
      rawRequirements = rawRequirements.requirements;
    }
    return Array.isArray(rawRequirements) ? rawRequirements.length : 0;
  };

  const hasAnyRequirements = getRequirementCount(requirements) > 0;
  const noChangesDetectedFromComparison = (() => {
    const summaries = [
      comparisonData?.comparison?.summary,
      comparisonData?.summary,
      comparisonData?.data?.comparison?.summary,
    ];
    for (const summary of summaries) {
      if (typeof summary?.total_changes === 'number') {
        return summary.total_changes === 0;
      }
    }
    return false;
  })();

  // Track whether the initial data load has completed at least once
  const initialLoadDone = useRef(false);

  // ============================================
  // DISPATCHER-BASED STREAMING (like Flutter)
  // Connect WS first, then send start_job, then listen
  // ============================================
  const reqDispatcher = useDispatcherJob({
    onComplete: async () => {
      // Reload requirements from API (like Flutter's onComplete callback)
      if (!trialId || !baseVersion || !targetVersion || !platform) return;
      try {
        const reqs = await apiService.getCachedRequirements(trialId, baseVersion, targetVersion, platform as 'app' | 'web', sessionId || undefined);
        if (reqs && reqs.requirements) {
          setRequirements(reqs);
        }
      } catch (err) {
        console.error('Failed to reload requirements after dispatcher job:', err);
      }
    },
  });

  const planDispatcher = useDispatcherJob({
    onComplete: async () => {
      if (!trialId || !baseVersion || !targetVersion || !platform) return;
      try {
        const planData = await apiService.getCachedPlan(trialId, baseVersion, targetVersion, platform as 'app' | 'web', sessionId || undefined);
        // API returns { success, trial_id, plan: { plan_id, actions, ... } }
        // The PlanActionsTable expects the inner plan object
        const planObj = planData?.plan || planData;
        if (planObj && (planObj.plan_id || planObj.actions)) {
          setPlan(planObj);
        }
      } catch (err) {
        // Plan reload after dispatcher job failed silently
      }
    },
  });

  const execDispatcher = useDispatcherJob({
    onComplete: async () => {
      if (!trialId || !baseVersion || !targetVersion || !platform || !sessionId) return;
      try {
        const summary = await apiService.getExecutionSummary(trialId, baseVersion, targetVersion, platform as 'app' | 'web', sessionId || undefined);
        if (summary && summary.status && summary.actions) {
          setExecutionSummary(summary);
          setProtocolGenerated(true);
        }
        if (summary && (summary.total_actions === 0 || summary.total_actions == null)) {
          try {
            const runningStatus = await apiService.getExecutionRunningStatus(trialId, baseVersion, targetVersion, platform as 'app' | 'web', sessionId || undefined, executionId || 'exec_001');
            if (runningStatus?.status === 'running') {
              setExecutionRunningStatus(runningStatus);
            } else {
              setExecutionRunningStatus(null);
            }
          } catch {
            setExecutionRunningStatus(null);
          }
        } else {
          setExecutionRunningStatus(null);
        }
      } catch (err) {
        console.error('Failed to reload execution summary after dispatcher job:', err);
      }
    },
  });

  // Poll execution running status every 30s when status is "running" until it stops
  const effectiveExecutionId = executionId || 'exec_001';
  useEffect(() => {
    if (executionRunningStatus?.status !== 'running' || !trialId || !baseVersion || !targetVersion || !platform || !sessionId) {
      return;
    }
    const intervalId = setInterval(async () => {
      try {
        const runningStatus = await apiService.getExecutionRunningStatus(
          trialId,
          baseVersion,
          targetVersion,
          platform as 'app' | 'web',
          sessionId,
          effectiveExecutionId
        );
        if (runningStatus?.status !== 'running') {
          setExecutionRunningStatus(null);
          const summary = await apiService.getExecutionSummary(
            trialId,
            baseVersion,
            targetVersion,
            platform as 'app' | 'web',
            sessionId
          );
          if (summary && summary.status && summary.actions) {
            setExecutionSummary(summary);
            setProtocolGenerated(true);
          }
        } else {
          setExecutionRunningStatus(runningStatus);
        }
      } catch {
        // Keep polling on transient errors
      }
    }, 20_000);
    return () => clearInterval(intervalId);
  }, [
    executionRunningStatus?.status,
    trialId,
    baseVersion,
    targetVersion,
    platform,
    sessionId,
    effectiveExecutionId,
  ]);

  // Load base version and cached data on mount or when platform changes
  const hasRoutePlatforms = Boolean(routePlatforms?.length);
  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      if (!trialId || !platform || !version) return;
      if (cancelled) return;

      try {
        // ---- Collect all data into local variables first ----
        // This prevents intermediate React renders that flash empty states

        // When accessing via direct URL (no navigation state), fetch trial platforms
        // to enable platform tabs even on direct URL access
        let loadedPlatforms: ('app' | 'web')[] | null = null;
        if (!hasRoutePlatforms) {
          try {
            const trialData = await apiService.getTrial(trialId);
            if (trialData?.platforms && trialData.platforms.length > 0) {
              // Platforms can be strings or objects with platform property
              const platforms = trialData.platforms.map((p: string | {platform: string}) => 
                typeof p === 'string' ? p : p.platform
              ).filter((p: string): p is 'app' | 'web' => p === 'app' || p === 'web');
              if (platforms.length > 0) {
                loadedPlatforms = platforms;
              }
            }
          } catch (err) {
            // Continue with single platform from URL if trial fetch fails
          }
        }
        if (cancelled) return;

        // Load PRS versions and select LAST version by date (no reasoning, just last)
        const prsVersions = await apiService.listPRSVersions(trialId, platform as 'app' | 'web');
        if (cancelled) return;

        // Convert to array and sort by date (from version_metadata or uploaded_at)
        const versionsArray = Object.entries(prsVersions).map(([versionKey, versionData]: [string, any]) => {
          // Get latest date from version_metadata array or use uploaded_at
          let latestDate = versionData.uploaded_at || '';
          if (versionData.version_metadata && Array.isArray(versionData.version_metadata) && versionData.version_metadata.length > 0) {
            // Find the latest date in version_metadata
            const dates = versionData.version_metadata
              .map((entry: any) => entry.date)
              .filter((d: string) => d)
              .sort()
              .reverse();
            if (dates.length > 0) {
              latestDate = dates[0];
            }
          }
          return {
            version: versionKey,
            data: versionData,
            date: latestDate
          };
        });

        // Sort by date (most recent last)
        versionsArray.sort((a, b) => {
          if (!a.date && !b.date) return 0;
          if (!a.date) return -1;
          if (!b.date) return 1;
          return a.date.localeCompare(b.date);
        });

        if (versionsArray.length === 0) {
          setError('No PRS versions found');
          return;
        }

        // Default fallback pair from available PRS versions.
        // Existing sessions should instead use source/target from session metadata.
        const fallbackTargetVersion = versionsArray[versionsArray.length - 1].version;
        const fallbackBaseVersion = versionsArray.length > 1
          ? versionsArray[versionsArray.length - 2].version
          : fallbackTargetVersion;

        const providedSessionId = parentSessionId || pathSessionId;
        let resolvedBaseVersion = fallbackBaseVersion;
        let resolvedTargetVersion = fallbackTargetVersion;
        let derivedSessionId = providedSessionId ?? `${fallbackBaseVersion}_to_${fallbackTargetVersion}`;

        // For existing sessions, always prefer the original session metadata versions.
        if (providedSessionId) {
          try {
            const sessionMetadata = await apiService.getSessionMetadata(trialId, providedSessionId);
            if (sessionMetadata?.source_prs_version && sessionMetadata?.target_prs_version) {
              resolvedBaseVersion = sessionMetadata.source_prs_version;
              resolvedTargetVersion = sessionMetadata.target_prs_version;
              derivedSessionId = sessionMetadata.session_id;
            }
          } catch (err) {
            // If metadata lookup fails, keep fallback derived from latest PRS list.
          }
        }

        // Check workflow mode - verify wizard is allowed
        try {
          const workflowMode = await apiService.getWorkflowMode(
            trialId,
            derivedSessionId,
            platform as 'app' | 'web'
          );
          if (!workflowMode.can_use_wizard) {
            setWizardBlocked(true);
            setWizardBlockedReason(
              workflowMode.wizard_disabled_reason ||
              'Wizard unavailable: Another workflow has pending work. Please commit or discard first.'
            );
            return; // Don't load data if wizard is blocked
          }
        } catch (err) {
          // Workflow mode check failed - continue anyway (fail open)
        }
        if (cancelled) return;

        if (cancelled) return;

        // Load all cached data into local variables (no setState calls yet)
        let loadedComparison: any = null;
        let loadedRequirements: any = null;
        let loadedPlan: any = null;
        let loadedSummary: any = null;
        let loadedProtocolGenerated = false;
        let loadedExecutionRunningStatus: ExecutionRunningStatus | null = null;
        let loadedExecutionId: string | null = null;
        let loadedIsCommitted = false;

        // Load cached comparison
        try {
          const comparison = await apiService.getCachedComparison(
            trialId,
            resolvedBaseVersion,
            resolvedTargetVersion,
            platform as 'app' | 'web',
            derivedSessionId
          );
          if (comparison?.success && comparison?.cached) {
            loadedComparison = comparison;
          }
        } catch (err) { /* ignore */ }

        // Load cached requirements
        try {
          const reqs = await apiService.getCachedRequirements(
            trialId,
            resolvedBaseVersion,
            resolvedTargetVersion,
            platform as 'app' | 'web',
            derivedSessionId
          );
          if (reqs && reqs.requirements) {
            loadedRequirements = reqs;
          }
        } catch (err) { /* ignore */ }

        // Load cached plan
        try {
          const planData = await apiService.getCachedPlan(
            trialId,
            resolvedBaseVersion,
            resolvedTargetVersion,
            platform as 'app' | 'web',
            derivedSessionId
          );
          // API returns { success, trial_id, plan: { plan_id, actions, ... } }
          const planObj = planData?.plan || planData;
          if (planObj && (planObj.plan_id || planObj.actions)) {
            loadedPlan = planObj;
          }
        } catch (err) { /* ignore */ }

        // Check for uncommitted execution (for commit/discard buttons)
        // Fetched first so we have the execution ID for subsequent calls
        try {
          const currentExecution = await apiService.getCurrentExecution(
            trialId,
            derivedSessionId,
            platform as 'app' | 'web'
          );
          if (currentExecution) {
            loadedExecutionId = currentExecution.execution_id;
            const committed = currentExecution.status === 'committed';
            const discarded = currentExecution.status === 'discarded';
            loadedIsCommitted = committed || discarded;
          }
        } catch (err) {
          // No current execution found
        }

        // Load execution summary if it exists
        try {
          const summary = await apiService.getExecutionSummary(
            trialId,
            resolvedBaseVersion,
            resolvedTargetVersion,
            platform as 'app' | 'web',
            derivedSessionId
          );
          if (summary && summary.status && summary.actions) {
            loadedSummary = summary;
            loadedProtocolGenerated = true;
          }
          if (summary && (summary.total_actions === 0 || summary.total_actions == null)) {
            try {
              const runningStatus = await apiService.getExecutionRunningStatus(
                trialId,
                resolvedBaseVersion,
                resolvedTargetVersion,
                platform as 'app' | 'web',
                derivedSessionId,
                loadedExecutionId || 'exec_001'
              );
              if (runningStatus?.status === 'running') {
                loadedExecutionRunningStatus = runningStatus;
              }
            } catch { /* ignore */ }
          }
        } catch (err) { /* ignore */ }

        if (cancelled) return;

        // ---- Apply all state at once in a single synchronous batch ----
        // React will batch all these into a single re-render
        setError(null);
        setWizardBlocked(false);
        setWizardBlockedReason(null);
        setBaseVersion(resolvedBaseVersion);
        setTargetVersion(resolvedTargetVersion);
        setSessionId(derivedSessionId);
        setSessionCreated(true);
        setComparisonData(loadedComparison);
        setRequirements(loadedRequirements);
        setPlan(loadedPlan);
        setExecutionSummary(loadedSummary);
        setProtocolGenerated(loadedProtocolGenerated);
        setExecutionRunningStatus(loadedExecutionRunningStatus ?? null);
        setExecutionId(loadedExecutionId);
        setIsExecutionCommitted(loadedIsCommitted);
        if (loadedPlatforms) {
          setFetchedPlatforms(loadedPlatforms);
        }
        initialLoadDone.current = true;
      } catch (err: any) {
        setError(err.message || 'Failed to load data');
        console.error('Error loading data:', err);
        initialLoadDone.current = true;
      } finally {
        // loading state managed by initialLoadDone ref
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [trialId, platform, version, parentSessionId, trailing, pathSessionId, hasRoutePlatforms]);

  const canNavigateToStepByRules = useCallback(
    (step: number, currentStep: number) => {
      if (step <= currentStep) return true;
      if (step === 1) return requirements !== null;
      if (step === 2) return hasAnyRequirements && plan !== null;
      if (step === 3) return protocolGenerated;
      return false;
    },
    [requirements, plan, hasAnyRequirements, protocolGenerated]
  );

  const goToWizardStep = useCallback(
    (step: number) => {
      if (!trialId || !version || !platform) return;
      if (!canNavigateToStepByRules(step, activeStep)) return;
      navigate(buildWizardPath(trialId, version, platform, sessionId, step), {state: location.state});
    },
    [
      trialId,
      version,
      platform,
      sessionId,
      activeStep,
      navigate,
      location.state,
      canNavigateToStepByRules,
    ]
  );

  // Keep URL and active step in sync; clamp URL to the furthest step allowed by loaded data.
  useEffect(() => {
    if (!trialId || !version || !platform) return;
    if (!initialLoadDone.current) return;
    if (wizardBlocked || error) return;

    const {stepSegment} = parseWizardTrailingPath(trailing);
    let stepIdx = wizardStepIndexFromSegment(stepSegment) ?? 0;

    const canAccessStep = (s: number) => {
      if (s === 0) return true;
      if (s === 1) return requirements !== null;
      if (s === 2) return hasAnyRequirements && plan !== null;
      if (s === 3) return protocolGenerated;
      return false;
    };

    while (stepIdx > 0 && !canAccessStep(stepIdx)) {
      stepIdx -= 1;
    }

    const desiredPath = buildWizardPath(trialId, version, platform, sessionId, stepIdx);
    if (location.pathname !== desiredPath) {
      navigate(desiredPath, {replace: true, state: location.state});
      return;
    }
    setActiveStep(stepIdx);
  }, [
    trialId,
    version,
    platform,
    trailing,
    sessionId,
    requirements,
    plan,
    protocolGenerated,
    hasAnyRequirements,
    wizardBlocked,
    error,
    navigate,
    location.pathname,
    location.state,
  ]);

  const handleBack = () => {
    navigate('/');
  };

  const handleGenerateRequirements = async () => {
    if (!trialId || !baseVersion || !targetVersion || !platform) return;

    const isRegenerate = requirements !== null;
    if (isRegenerate) {
      setRequirements(null);
      setPlan(null);
      setExecutionSummary(null);
      setProtocolGenerated(false);
      setExecutionId(null);
      setIsExecutionCommitted(false);
    }

    // Use dispatcher pattern (like Flutter): connect first, then send start_job
    setReqGenerationDialogOpen(true);
    reqDispatcher.startJob('build_requirements', {
      trial_id: trialId,
      session_id: sessionId || `${baseVersion}_to_${targetVersion}`,
      version1: baseVersion,
      version2: targetVersion,
      platform: platform as 'app' | 'web',
      override: isRegenerate,
    });
  };

  const handleRequirementsSuccess = async () => {
    // Reload requirements from API
    if (!trialId || !baseVersion || !targetVersion || !platform) return;

    try {
      const reqs = await apiService.getCachedRequirements(trialId, baseVersion, targetVersion, platform as 'app' | 'web', sessionId || undefined);
      if (reqs && reqs.requirements) {
        setRequirements(reqs);
      }
    } catch (err: any) {
      console.error('Failed to reload requirements:', err);
      setError('Requirements generated but failed to reload. Please refresh the page.');
    }
  };

  const handleRequirementsEdited = async () => {
    if (!trialId || !baseVersion || !targetVersion || !platform) return;
    try {
      const reqs = await apiService.getCachedRequirements(trialId, baseVersion, targetVersion, platform as 'app' | 'web', sessionId || undefined);
      if (reqs && reqs.requirements) {
        setRequirements(reqs);
      }
    } catch (err) {
      console.error('Failed to refresh requirements after edit:', err);
    }
  };

  const handleGeneratePlan = async () => {
    if (!trialId || !baseVersion || !targetVersion || !platform) return;
    if (!hasAnyRequirements) {
      setSnackbar({
        open: true,
        message: 'No valid requirements found. Generate requirements with changes before creating a plan.',
        severity: 'error',
      });
      return;
    }

    const isRegenerate = plan !== null;
    if (isRegenerate) {
      setPlan(null);
      setExecutionSummary(null);
      setProtocolGenerated(false);
      setExecutionId(null);
      setIsExecutionCommitted(false);
    }

    // Use dispatcher pattern (like Flutter): connect first, then send start_job
    setPlanGenerationDialogOpen(true);
    planDispatcher.startJob('generate_plan', {
      trial_id: trialId,
      session_id: sessionId || `${baseVersion}_to_${targetVersion}`,
      platform: platform as 'app' | 'web',
      override: isRegenerate,
    });
  };

  const handlePlanSuccess = async () => {
    // Reload plan from API
    if (!trialId || !baseVersion || !targetVersion || !platform) return;

    try {
      const planData = await apiService.getCachedPlan(trialId, baseVersion, targetVersion, platform as 'app' | 'web', sessionId || undefined);
      // API returns { success, trial_id, plan: { plan_id, actions, ... } }
      const planObj = planData?.plan || planData;
      if (planObj && (planObj.plan_id || planObj.actions)) {
        setPlan(planObj);
      }
    } catch (err: any) {
      console.error('Failed to reload plan:', err);
      setError('Plan generated but failed to reload. Please refresh the page.');
    }
  };

  // Handler for coordinating navigation from PlanActionsTable
  const handleEditRequirementFromPlan = useCallback(
    (_requirementId: string) => {
      if (!trialId || !version || !platform) return;
      navigate(buildWizardPath(trialId, version, platform, sessionId, 0), {state: location.state});
    },
    [trialId, version, platform, sessionId, navigate, location.state]
  );

  const handleExecuteProtocol = async () => {
    if (!trialId || !baseVersion || !targetVersion || !platform) return;

    const isRegenerate = protocolGenerated;
    if (isRegenerate) {
      setExecutionSummary(null);
      setProtocolGenerated(false);
      setExecutionId(null);
      setIsExecutionCommitted(false);
    }
    setExecutionRunningStatus(null);
    // Use dispatcher pattern (like Flutter): connect first, then send start_job
    setExecutionDialogOpen(true);
    execDispatcher.startJob('execute_plan', {
      trial_id: trialId,
      session_id: sessionId || `${baseVersion}_to_${targetVersion}`,
      platform: platform as 'app' | 'web',
    });
  };

  const handleExecutionSuccess = async () => {
    // Reload execution summary from API
    if (!trialId || !baseVersion || !targetVersion || !platform || !sessionId) return;

    // Fetch execution ID first for commit/discard flow and to pass to status checks
    let currentExecutionId: string | null = null;
    try {
      const currentExecution = await apiService.getCurrentExecution(
        trialId,
        sessionId,
        platform as 'app' | 'web'
      );
      if (currentExecution) {
        currentExecutionId = currentExecution.execution_id;
        setExecutionId(currentExecution.execution_id);
        setIsExecutionCommitted(false);
      }
    } catch (err) {
      // Could not fetch execution ID after execution
    }

    try {
      const summary = await apiService.getExecutionSummary(trialId, baseVersion, targetVersion, platform as 'app' | 'web', sessionId || undefined);
      if (summary && summary.status && summary.actions) {
        setExecutionSummary(summary);
        setProtocolGenerated(true);
      }
      if (summary && (summary.total_actions === 0 || summary.total_actions == null)) {
        try {
          const runningStatus = await apiService.getExecutionRunningStatus(trialId, baseVersion, targetVersion, platform as 'app' | 'web', sessionId || undefined, currentExecutionId || undefined);
          if (runningStatus?.status === 'running') {
            setExecutionRunningStatus(runningStatus);
          } else {
            setExecutionRunningStatus(null);
          }
        } catch {
          setExecutionRunningStatus(null);
        }
      } else {
        setExecutionRunningStatus(null);
      }
    } catch (err: any) {
      console.error('Failed to reload execution summary:', err);
      // Still mark as generated even if summary load fails
      setProtocolGenerated(true);
    }
  };

  const handleViewDiff = (action: any) => {
    setSelectedAction(action);
    setDiffDialogOpen(true);
  };

  const handleDownloadProtocol = async () => {
    if (!trialId || !baseVersion || !targetVersion || !platform) return;

    try {
      setDownloading(true);
      setError(null); // Clear any previous errors

      const blob = await apiService.downloadProtocol(trialId, baseVersion, targetVersion, platform as 'app' | 'web', sessionId || undefined);

      // file-saver automatically saves to browser's default download location (usually ~/Downloads)
      saveAs(blob, `${trialId}_${targetVersion}_protocol.zip`);
    } catch (err: any) {
      setError(`Failed to download protocol: ${err.message || 'Unknown error'}`);
    } finally {
      setDownloading(false);
    }
  };

  // ============================================
  // COMMIT / DISCARD HANDLERS
  // ============================================

  const hasUncommittedExecution = protocolGenerated && executionId && !isExecutionCommitted;

  const handleCommitExecution = async () => {
    if (!trialId || !sessionId || !executionId || !platform) return;

    setCommitDialogOpen(false);
    setIsCommitting(true);

    try {
      await apiService.commitExecution(
        trialId,
        sessionId,
        platform as 'app' | 'web',
        executionId
      );
      setIsExecutionCommitted(true);
      setSnackbar({open: true, message: 'Changes committed successfully!', severity: 'success'});
    } catch (err: any) {
      setSnackbar({open: true, message: `Failed to commit: ${err.message || 'Unknown error'}`, severity: 'error'});
      console.error('Failed to commit execution:', err);
    } finally {
      setIsCommitting(false);
    }
  };

  const handleDiscardExecution = async () => {
    if (!trialId || !version || !sessionId || !executionId || !platform) return;

    setDiscardDialogOpen(false);
    setIsDiscarding(true);

    try {
      await apiService.discardExecution(
        trialId,
        sessionId,
        platform as 'app' | 'web',
        executionId
      );

      // Reset execution state and go back to Plan step
      setExecutionId(null);
      setIsExecutionCommitted(false);
      setExecutionSummary(null);
      setExecutionRunningStatus(null);
      setProtocolGenerated(false);
      navigate(buildWizardPath(trialId, version, platform, sessionId, 1), {state: location.state});

      setSnackbar({open: true, message: 'Changes discarded. Returned to Plan step.', severity: 'success'});
    } catch (err: any) {
      setSnackbar({open: true, message: `Failed to discard: ${err.message || 'Unknown error'}`, severity: 'error'});
      console.error('Failed to discard execution:', err);
    } finally {
      setIsDiscarding(false);
    }
  };

  // Show a full-page loading overlay until the initial data load completes.
  // Uses a ref so React Strict Mode double-rendering cannot bypass it.
  if (!initialLoadDone.current) {
    return <ProgressOverlay inProgress message="Loading wizard data..." />;
  }

  if (wizardBlocked) {
    return (
      <Container maxWidth={false} sx={{mt: 4, mb: 4, px: 3}}>
        <Alert severity="warning" sx={{mb: 2}}>
          {wizardBlockedReason || 'Wizard is currently unavailable.'}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={handleBack}>
          Back to Protocols
        </Button>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth={false} sx={{mt: 4, mb: 4, px: 3}}>
        <Alert severity="error" sx={{mb: 2}}>
          {error}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={handleBack}>
          Back to Protocols
        </Button>
      </Container>
    );
  }

  const canGoNext = () => {
    if (activeStep === 0) return requirements !== null;
    if (activeStep === 1) return plan !== null;
    if (activeStep === 2) return protocolGenerated;
    return true;
  };

  const canNavigateToStep = (step: number): boolean => {
    // Can always go back to current or previous steps
    if (step <= activeStep) return true;

    // Check if we can navigate forward to this step
    if (step === 1) return requirements !== null; // Can go to Plan when requirements are loaded
    if (step === 2) return hasAnyRequirements && plan !== null; // Can go to Generate if plan exists
    if (step === 3) return protocolGenerated; // Can go to Validate if protocol is generated

    return false;
  };

  const handleStepClick = (step: number) => {
    goToWizardStep(step);
  };

  const isStepCompleted = (step: number): boolean => {
    // A step is completed if it has the required data
    if (step === 0) return requirements !== null;
    if (step === 1) return plan !== null;
    if (step === 2) return protocolGenerated;
    if (step === 3) return false; // Step 4 (Validate) - not implemented yet
    return false;
  };

  // Render wizard content for each platform (same for APP and WEB)
  const renderPlatformWizard = (currentPlatform: 'app' | 'web') => {
    // Both APP and WEB platforms use the same wizard - only platform parameter differs
    return (
      <Box sx={{ position: 'relative' }}>
        {/* Progress Steps with nav arrows (matching Flutter) */}
        <Box sx={{display: 'flex', alignItems: 'center', mb: 2, mt: 3}}>
          <Tooltip title="Previous Step">
            <span>
              <IconButton
                onClick={() => goToWizardStep(Math.max(0, activeStep - 1))}
                disabled={activeStep === 0}
                size="small"
                sx={{
                  border: 1,
                  borderColor: activeStep === 0 ? 'action.disabledBackground' : 'divider',
                  color: activeStep === 0 ? 'text.disabled' : 'text.secondary',
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                }}
              >
                <BackIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Box sx={{flex: 1, mx: 2}}>
            <WizardSteps
              activeStep={activeStep}
              steps={WIZARD_STEPS}
              onStepClick={handleStepClick}
              canNavigateToStep={canNavigateToStep}
              isStepCompleted={isStepCompleted}
            />
          </Box>

          <Tooltip title="Next Step">
            <span>
              <IconButton
                onClick={() => goToWizardStep(Math.min(WIZARD_STEPS.length - 1, activeStep + 1))}
                disabled={!canGoNext() || activeStep === WIZARD_STEPS.length - 1}
                size="small"
                sx={{
                  border: 1,
                  borderColor: (!canGoNext() || activeStep === WIZARD_STEPS.length - 1) ? 'action.disabledBackground' : 'primary.main',
                  color: (!canGoNext() || activeStep === WIZARD_STEPS.length - 1) ? 'text.disabled' : 'primary.main',
                  bgcolor: (!canGoNext() || activeStep === WIZARD_STEPS.length - 1) ? 'transparent' : 'primary.main',
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                  '&:not(:disabled)': {
                    color: 'common.white',
                  },
                  '&:hover:not(:disabled)': {
                    bgcolor: 'primary.dark',
                  },
                }}
              >
                <ForwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Content Area */}
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            minHeight: '400px',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
        {/* Step Header - only show when step has content (empty states have their own buttons) */}
        {activeStep === 1 && plan && (
          <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2}}>
            <Tooltip title="Regenerate plan from requirements">
              <span>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleGeneratePlan}
                  disabled={!requirements}
                  sx={{textTransform: 'none'}}
                >
                  Re-Generate Plan
                </Button>
              </span>
            </Tooltip>
          </Box>
        )}
        {activeStep === 2 && (protocolGenerated || executionSummary || executionRunningStatus?.status === 'running') && (
          <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mb: 2}}>
            {protocolGenerated && (
              <Tooltip title={hasUncommittedExecution ? 'Commit changes before downloading' : 'Download generated protocol as ZIP'}>
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleDownloadProtocol}
                    disabled={downloading || !!hasUncommittedExecution}
                    startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
                    sx={{textTransform: 'none'}}
                  >
                    {downloading ? 'Downloading...' : 'Download Protocol ZIP'}
                  </Button>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Execute the surgical plan to generate protocol modifications">
              <span>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleExecuteProtocol}
                  disabled={!plan}
                  color="success"
                  sx={{textTransform: 'none'}}
                >
                  Re-Execute Plan
                </Button>
              </span>
            </Tooltip>
          </Box>
        )}

        {/* Step Content */}
        {activeStep === 0 && (
          <>
            <RequirementsEditor
              requirementsData={
                requirements
                  ? {
                      ...requirements,
                      _uiNoChangesDetected: noChangesDetectedFromComparison,
                    }
                  : requirements
              }
              onRequirementsChanged={handleRequirementsEdited}
              onRegenerate={handleGenerateRequirements}
              onViewComparison={comparisonData ? () => setComparisonDialogOpen(true) : undefined}
              hasComparison={!!comparisonData}
            />
            
            {/* Instructions Section */}
            {sessionId && (
              <Box sx={{mt: 3}}>
                <InstructionsEditor
                  trialId={trialId || ''}
                  sessionId={sessionId}
                  platform={currentPlatform}
                  step="requirements"
                  title="Requirements Instructions"
                  placeholder="Add specific instructions for generating requirements..."
                  disabled={!sessionId}
                />
              </Box>
            )}
          </>
        )}

        {activeStep === 1 && (
          <>
            {plan ? (
              <>
                <PlanActionsTable
                  plan={plan}
                  cached={true}
                  onEditRequirementRequested={handleEditRequirementFromPlan}
                />
                
                {/* Instructions Section */}
                {sessionId && (
                  <Box sx={{mt: 3}}>
                    <InstructionsEditor
                      trialId={trialId || ''}
                      sessionId={sessionId}
                      platform={currentPlatform}
                      step="plan"
                      title="Plan Instructions"
                      placeholder="Add specific instructions for generating the surgical plan..."
                      disabled={!sessionId}
                    />
                  </Box>
                )}
              </>
            ) : (
              <>
                <Box sx={{
                  py: 8,
                  px: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <AccountTreeIcon sx={{fontSize: 64, color: 'grey.400', mb: 2}} />
                  <Typography sx={{fontSize: '1.25rem', fontWeight: 600, color: 'text.primary', mb: 1}}>
                    No Plan Generated
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{mb: 3, maxWidth: 400, textAlign: 'center'}}
                  >
                    {requirements
                      ? 'Generate a surgical modification plan based on the requirements.'
                      : 'Complete Step 1 first to generate requirements.'}
                  </Typography>
                  {hasAnyRequirements && (
                    <Button
                      variant="contained"
                      startIcon={<PlayArrowIcon />}
                      onClick={handleGeneratePlan}
                      sx={{textTransform: 'none', px: 3, py: 1.5}}
                    >
                      Generate Plan
                    </Button>
                  )}
                </Box>
                
                {/* Instructions Section - show even when no plan */}
                {sessionId && (
                  <Box sx={{mt: 3}}>
                    <InstructionsEditor
                      trialId={trialId || ''}
                      sessionId={sessionId}
                      platform={currentPlatform}
                      step="plan"
                      title="Plan Instructions"
                      placeholder="Add specific instructions for generating the surgical plan..."
                      disabled={!sessionId}
                    />
                  </Box>
                )}
              </>
            )}
          </>
        )}

        {activeStep === 2 && (
          <>
            {executionRunningStatus?.status === 'running' ? (
              <>
                <Box
                  sx={{
                    p: 2.5,
                    mb: 3,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    border: 1,
                    borderColor: 'info.light',
                    bgcolor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(2,136,209,0.08)'
                        : 'rgba(227,242,253,1)',
                  }}
                >
                  <CircularProgress size={24} sx={{flexShrink: 0, mt: 0.25}} />
                  <Box sx={{flex: 1}}>
                    <Typography sx={{fontWeight: 600, color: 'info.dark'}}>
                      Execution is running
                    </Typography>
                    <Typography variant="body2" sx={{color: 'info.main', mt: 0.5, whiteSpace: 'pre-line'}}>
                      {executionRunningStatus.message || 'The protocol execution is in progress. Summary will appear when complete.'}
                    </Typography>
                  </Box>
                </Box>
                {sessionId && (
                  <Box sx={{mt: 3}}>
                    <InstructionsEditor
                      trialId={trialId || ''}
                      sessionId={sessionId}
                      platform={currentPlatform}
                      step="execution"
                      title="Execution Instructions"
                      placeholder="Add specific instructions for protocol execution..."
                      disabled={!sessionId}
                    />
                  </Box>
                )}
              </>
            ) : protocolGenerated && executionSummary ? (
              <>
                {/* Status alert (matching Flutter) */}
                {(() => {
                  const executableActions = executionSummary.actions?.filter(
                    (a: any) => a.action !== 'note' && a.action !== 'unresolved'
                  ) || [];
                  const actualFailed = executableActions.filter((a: any) => a.status === 'failed').length;
                  const hasFailures = actualFailed > 0;

                  return (
                    <Box
                      sx={{
                        p: 2.5,
                        mb: 3,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        border: 1,
                        borderColor: hasFailures ? 'error.light' : 'success.light',
                        bgcolor: (theme) =>
                          hasFailures
                            ? theme.palette.mode === 'dark'
                              ? 'rgba(211,47,47,0.06)'
                              : 'rgba(232,245,233,1)'
                            : theme.palette.mode === 'dark'
                              ? 'rgba(46,125,50,0.06)'
                              : 'rgba(232,245,233,1)',
                      }}
                    >
                      {hasFailures ? (
                        <WarningIcon sx={{color: 'error.main', fontSize: 28}} />
                      ) : (
                        <CommitIcon sx={{color: 'success.main', fontSize: 28}} />
                      )}
                      <Box sx={{flex: 1}}>
                        <Typography sx={{fontWeight: 600, color: hasFailures ? 'error.dark' : 'success.dark'}}>
                          {hasFailures
                            ? `Some actions failed during protocol generation`
                            : 'Protocol modifications applied successfully'}
                        </Typography>
                        <Typography variant="body2" sx={{color: hasFailures ? 'error.main' : 'success.main', mt: 0.5}}>
                          {hasFailures
                            ? 'Review the execution summary below to see which actions failed and why.'
                            : 'Review the execution summary below or download the generated protocol.'}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })()}
                <ExecutionSummaryTable
                  summary={executionSummary}
                  onViewDiff={handleViewDiff}
                />

                {/* Instructions Section */}
                {sessionId && (
                  <Box sx={{mt: 3}}>
                    <InstructionsEditor
                      trialId={trialId || ''}
                      sessionId={sessionId}
                      platform={currentPlatform}
                      step="execution"
                      title="Execution Instructions"
                      placeholder="Add specific instructions for protocol execution..."
                      disabled={!sessionId}
                    />
                  </Box>
                )}

                {/* Commit/Discard actions - contextual to execution results */}
                {hasUncommittedExecution && (
                  <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3, pt: 2, borderTop: 1, borderColor: 'divider'}}>
                    <Tooltip title="Discard changes and return to Plan step">
                      <span>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={isDiscarding ? <CircularProgress size={16} color="inherit" /> : <DiscardIcon />}
                          onClick={() => setDiscardDialogOpen(true)}
                          disabled={isCommitting || isDiscarding}
                          sx={{textTransform: 'none'}}
                        >
                          {isDiscarding ? 'Discarding...' : 'Discard Changes'}
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title="Commit changes to the protocol">
                      <span>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={isCommitting ? <CircularProgress size={16} color="inherit" /> : <CommitIcon />}
                          onClick={() => setCommitDialogOpen(true)}
                          disabled={isCommitting || isDiscarding}
                          sx={{textTransform: 'none'}}
                        >
                          {isCommitting ? 'Committing...' : 'Commit Changes'}
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                )}

              </>
            ) : protocolGenerated ? (
              <>
                <Box
                  sx={{
                    p: 2.5,
                    mb: 2,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    border: 1,
                    borderColor: 'success.light',
                    bgcolor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(46,125,50,0.06)'
                        : 'rgba(232,245,233,1)',
                  }}
                >
                  <CommitIcon sx={{color: 'success.main', fontSize: 28}} />
                  <Box>
                    <Typography sx={{fontWeight: 600, color: 'success.dark'}}>
                      Protocol Generated Successfully
                    </Typography>
                    <Typography variant="body2" sx={{color: 'success.main', mt: 0.5}}>
                      The protocol modifications have been applied successfully. Click &quot;Re-Execute Plan&quot; to regenerate, or proceed to the next step for validation.
                    </Typography>
                  </Box>
                </Box>
                
                {/* Instructions Section */}
                {sessionId && (
                  <Box sx={{mt: 3}}>
                    <InstructionsEditor
                      trialId={trialId || ''}
                      sessionId={sessionId}
                      platform={currentPlatform}
                      step="execution"
                      title="Execution Instructions"
                      placeholder="Add specific instructions for protocol execution..."
                      disabled={!sessionId}
                    />
                  </Box>
                )}
              </>
            ) : (
              <>
                <Box sx={{
                  py: 8,
                  px: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <PlayCircleIcon sx={{fontSize: 64, color: 'grey.400', mb: 2}} />
                  <Typography sx={{fontSize: '1.25rem', fontWeight: 600, color: 'text.primary', mb: 1}}>
                    Protocol Not Generated
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{mb: 3, maxWidth: 400, textAlign: 'center'}}
                  >
                    {plan
                      ? 'Execute the modification plan to generate the updated protocol.'
                      : 'Complete Step 2 first to generate the plan.'}
                  </Typography>
                  {plan && (
                    <Button
                      variant="contained"
                      startIcon={<PlayArrowIcon />}
                      onClick={handleExecuteProtocol}
                      sx={{textTransform: 'none', px: 3, py: 1.5}}
                    >
                      Execute Plan
                    </Button>
                  )}
                </Box>
                
                {/* Instructions Section - show even when no protocol */}
                {sessionId && (
                  <Box sx={{mt: 3}}>
                    <InstructionsEditor
                      trialId={trialId || ''}
                      sessionId={sessionId}
                      platform={currentPlatform}
                      step="execution"
                      title="Execution Instructions"
                      placeholder="Add specific instructions for protocol execution..."
                      disabled={!sessionId}
                    />
                  </Box>
                )}
              </>
            )}
          </>
        )}

        {activeStep >= 3 && (
          <Box sx={{
            py: 8,
            px: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <VerifiedIcon sx={{fontSize: 64, color: 'grey.400', mb: 2}} />
            <Typography sx={{fontSize: '1.25rem', fontWeight: 600, color: 'text.primary', mb: 1}}>
              Validation
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{maxWidth: 400, textAlign: 'center'}}
            >
              Final validation checks will be available in a future release.
            </Typography>
          </Box>
        )}
        </Paper>
      </Box>
    );
  };

  return (
    <Container maxWidth={false} sx={{mt: 2, mb: 4, px: 3}}>
      {/* Trial Top Bar */}
      <TrialTopBar
        trialId={trialId || ''}
        version={targetVersion || ''}
        sessionName={sessionId || undefined}
        platform={platform}
        onBack={handleBack}
        viewType="wizard"
      />

      {/* Platform Tabs Layout */}
      <PlatformTabsLayout
        platforms={trialPlatforms}
        renderTabContent={renderPlatformWizard}
        trialId={trialId || ''}
        version={targetVersion || ''}
        sessionId={sessionId || undefined}
        wizardStepSegment={wizardSegmentFromStepIndex(activeStep)}
      />

      {/* Dialogs */}
      <ComparisonDialog
        open={comparisonDialogOpen}
        onClose={() => setComparisonDialogOpen(false)}
        comparisonData={comparisonData}
        baseVersion={baseVersion || undefined}
        newVersion={targetVersion || ''}
      />

      {/* Progress dialogs now use dispatcher pattern (like Flutter) */}
      <RequirementsGenerationDialog
        open={reqGenerationDialogOpen}
        onClose={() => { setReqGenerationDialogOpen(false); reqDispatcher.reset(); }}
        onSuccess={handleRequirementsSuccess}
        jobId={reqGenerationJobId}
        trialId={trialId || ''}
        version1={baseVersion || ''}
        version2={targetVersion || ''}
        dispatcherMessages={reqDispatcher.progressMessages}
        dispatcherStatus={reqDispatcher.status}
        dispatcherError={reqDispatcher.error}
      />

      <PlanGenerationDialog
        open={planGenerationDialogOpen}
        onClose={() => { setPlanGenerationDialogOpen(false); planDispatcher.reset(); }}
        onSuccess={handlePlanSuccess}
        jobId={planGenerationJobId}
        trialId={trialId || ''}
        sessionId={sessionId || `${baseVersion}_to_${targetVersion}`}
        dispatcherMessages={planDispatcher.progressMessages}
        dispatcherStatus={planDispatcher.status}
        dispatcherError={planDispatcher.error}
      />

      <ProtocolExecutionDialog
        open={executionDialogOpen}
        onClose={() => { setExecutionDialogOpen(false); execDispatcher.reset(); }}
        onSuccess={handleExecutionSuccess}
        jobId={executionJobId}
        trialId={trialId || ''}
        sessionId={sessionId || `${baseVersion}_to_${targetVersion}`}
        dispatcherMessages={execDispatcher.progressMessages}
        dispatcherStatus={execDispatcher.status}
        dispatcherError={execDispatcher.error}
      />

      <XMLDiffDialog
        open={diffDialogOpen}
        action={selectedAction}
        onClose={() => setDiffDialogOpen(false)}
      />

      {/* Commit Confirmation Dialog (modular-ui) */}
      <ModularDialog
        open={commitDialogOpen}
        title={<Box sx={{pl: 3, pr: 3}}>Commit Changes</Box>}
        content={
          <Box sx={{pl: 3, pr: 3, pb: 1}}>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to commit these changes to the protocol?
            </Typography>
            <Typography variant="body2" sx={{mt: 1}}>This action will:</Typography>
            <Box component="ul" sx={{mt: 0, mb: 1}}>
              <li><Typography variant="body2">Apply all executed changes to the protocol</Typography></li>
              <li><Typography variant="body2">Make the wizard view-only</Typography></li>
            </Box>
            <Typography variant="body2" color="error">
              This cannot be undone.
            </Typography>
          </Box>
        }
        dialogPaperSx={{width: '100%', maxHeight: '100%', pt: 3, pb: 3, pl: 0, pr: 0}}
        dialogTitleSx={{p: 0}}
        dialogContentSx={{p: 0}}
        dialogActionsSx={{pl: 3, pr: 3, justifyContent: 'space-between', pt: 4, pb: 0}}
        actionButtonProps={[
          {
            children: 'Cancel',
            variant: 'outlined',
            onClick: () => setCommitDialogOpen(false),
          },
          {
            children: isCommitting ? 'Committing...' : 'Commit',
            variant: 'contained',
            color: 'success',
            onClick: handleCommitExecution,
            disabled: isCommitting,
          },
        ]}
      />

      {/* Discard Confirmation Dialog (modular-ui) */}
      <ModularDialog
        open={discardDialogOpen}
        title={<Box sx={{pl: 3, pr: 3}}>Discard Changes</Box>}
        content={
          <Box sx={{pl: 3, pr: 3, pb: 1}}>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to discard these changes?
            </Typography>
            <Typography variant="body2" sx={{mt: 1}}>This action will:</Typography>
            <Box component="ul" sx={{mt: 0, mb: 1}}>
              <li><Typography variant="body2">Delete all staged changes</Typography></li>
              <li><Typography variant="body2">Return to the Plan step</Typography></li>
              <li><Typography variant="body2">Allow you to regenerate the plan or modify requirements</Typography></li>
            </Box>
            <Typography variant="body2" color="error">
              This cannot be undone.
            </Typography>
          </Box>
        }
        dialogPaperSx={{width: '100%', maxHeight: '100%', pt: 3, pb: 3, pl: 0, pr: 0}}
        dialogTitleSx={{p: 0}}
        dialogContentSx={{p: 0}}
        dialogActionsSx={{pl: 3, pr: 3, justifyContent: 'space-between', pt: 4, pb: 0}}
        actionButtonProps={[
          {
            children: 'Cancel',
            variant: 'outlined',
            onClick: () => setDiscardDialogOpen(false),
          },
          {
            children: isDiscarding ? 'Discarding...' : 'Discard',
            variant: 'contained',
            color: 'error',
            onClick: handleDiscardExecution,
            disabled: isDiscarding,
          },
        ]}
      />

      {/* ProgressOverlay for committing/discarding */}
      <ProgressOverlay
        inProgress={isCommitting || isDiscarding}
        message={isCommitting ? 'Committing changes...' : 'Discarding changes...'}
        fullOverlay
      />

      {/* Notifications */}
      <Notification
        snackbarProps={{
          open: snackbar.open,
          autoHideDuration: 4000,
          onClose: () => setSnackbar(prev => ({...prev, open: false})),
          anchorOrigin: {vertical: 'top', horizontal: 'center'},
        }}
        alertProps={{
          onClose: () => setSnackbar(prev => ({...prev, open: false})),
          severity: snackbar.severity,
        }}
      >
        {snackbar.message}
      </Notification>
    </Container>
  );
};

const ProtocolGeneratorWizardPage: React.FC = () => {
  return (
    <WizardNavigationProvider>
      <WizardPageContent />
    </WizardNavigationProvider>
  );
};

export default ProtocolGeneratorWizardPage;
